import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { StoreConnectionError } from "@/components/shared/StoreConnectionError";

import { Meta } from "@/@types/paginated";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import { EmptyTable } from "components/Datatable/components/EmptyTable";
import { useStoreAccessFilterActions } from "../../filter/hooks/actions";
import { useStoreAccessFilterState } from "../../filter/hooks/filterState";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /**
   * Metadados de paginação. Aceita objeto vazio porque os callers fazem
   * `{ data = [], meta = {} } = accessData ?? {}` antes dos primeiros dados
   * chegarem do endpoint.
   */
  meta: Partial<Meta>;
  isLoading?: boolean;
  connectionError?: string;
  onRetry?: () => void;
  filterSlot?: React.ReactNode;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  meta,
  isLoading = false,
  connectionError,
  onRetry,
  filterSlot,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const { storeCode, activeTab, users, operators } = useStoreAccessFilterState();
  const { updateTabFilter } = useStoreAccessFilterActions();
  const activeFilters = activeTab === "users" ? users : operators;

  // Sort é server-side e per-tab — TanStack só reflete `operators.sortBy/
  // sortOrder` (ou `users.*`).
  const sorting = React.useMemo<SortingState>(
    () =>
      activeFilters.sortBy
        ? [
            {
              id: activeFilters.sortBy,
              desc: activeFilters.sortOrder === "DESC",
            },
          ]
        : [],
    [activeFilters.sortBy, activeFilters.sortOrder],
  );

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    const first = next[0];
    if (!first) {
      // Usuário zerou o sort (toggle do mesmo header 3x). Volta ao default
      // aceito pelo backend (`SortByOperatorEnum.NAME` / ASC).
      updateTabFilter({ tab: activeTab, key: "sortBy", value: "name" });
      updateTabFilter({ tab: activeTab, key: "sortOrder", value: "ASC" });
      updateTabFilter({ tab: activeTab, key: "page", value: 1 });
      return;
    }
    updateTabFilter({ tab: activeTab, key: "sortBy", value: first.id });
    updateTabFilter({
      tab: activeTab,
      key: "sortOrder",
      value: first.desc ? "DESC" : "ASC",
    });
    updateTabFilter({ tab: activeTab, key: "page", value: 1 });
  };

  const hasActiveFilters = () => {
    return !!(
      storeCode ||
      activeFilters.search ||
      activeFilters.code ||
      activeFilters.classification ||
      activeFilters.blocked !== null ||
      (activeFilters.cpfFilter && activeFilters.cpfFilter !== "valid")
    );
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: true,
    manualSorting: true,
    pageCount: meta?.lastPage || 1,
  });

  const hasData = table.getRowModel().rows?.length > 0;
  const showPagination = hasData && !connectionError && !isLoading;

  return (
    <div className="space-y-4">
      <DataTableToolbar table={table} filterSlot={filterSlot} />
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableLoad colSpan={columns.length} />
            ) : connectionError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-0">
                  <StoreConnectionError
                    storeCode={String(storeCode || "")}
                    onRetry={onRetry}
                  />
                </TableCell>
              </TableRow>
            ) : hasData ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {hasActiveFilters() ? (
                    <div className="text-muted-foreground">
                      Nenhum resultado encontrado para os filtros aplicados.
                    </div>
                  ) : (
                    <EmptyTable />
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {showPagination && (
        <DataTablePagination table={table} meta={meta as Meta} />
      )}
    </div>
  );
}
